import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import axios from "axios";
import { useTranslation } from 'react-i18next';
import EventPopover from './EventPopover.jsx';
import { createPdfRoot, LocationsCalendarPdfComponent, unmountPdfRoot } from './LocationsCalendarPdf.jsx';
import NoResultCard from '../../common/NoResultCard.jsx';
import Select from '../../common/inputs/Select.jsx';
import GcDatePicker from '../../common/inputs/GcDatePicker.jsx';
import Dropdown from '../../common/dropdowns/Dropdown.jsx';
import DropdownSelect from '../../common/dropdowns/DropdownSelect.jsx';
import { Box, Skeleton, TextField, Typography } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { isEmptyObject, persistStateToSession, selectedValuesToArray } from '../../../../utils.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ThemeProvider, styled as muiStyled } from '@mui/material/styles';
import { backYouTheme } from '../../../../theme.ts';
import './LocationsCalendar.scss';

const SESSION_STORAGE_KEY = 'locations-calendar-filters';
const DEBUG_PDF_QUERY_PARAM = 'test';
const RESOURCE_AREA_WIDTH = '220px';

let timelineDrag = {
	mouseDown: false,
	startX: null,
	scrollLeft: null
}

export default function LocationsCalendar(props) {
	const {
		id,
		dateStart = new Date(),
		initialView = 'semaine',
		resizeHeight = false,
		hotelsId = null,
		requestsHotelsId = null,
		excludeRequestsHotelsId = null,
		productsId = null,
		baseCurrentUrl = null,
		showPopoverLinks = false,
		variablesEvents = null,
		calendarOnly = false,
		onLoad = () => false
	} = props;

	const { t: gctrad } = useTranslation();
	const calendarRef = useRef(null);
	const printRef = useRef(null);
	const linkBloquerSalleRef = useRef(null);
	const linkGererEspacesRef = useRef(null);

	const [isCalendarLoaded, setIsCalendarLoaded] = useState(false);
	const [currentView, setCurrentView] = useState(null);
	const [openDateSelect, setOpenDateSelect] = useState(false);
	const [startDate, setStartDate] = useState(null);
	const [endDate, setEndDate] = useState(null);
	const [slotMinWidth, setSlotMinWidth] = useState(null);
	const [resourceAreaWidth, setResourceAreaWidth] = useState(RESOURCE_AREA_WIDTH);
	const [hotelsList, setHotelsList] = useState([]);
	const [hotelsListSelected, setHotelsListSelected] = useState(null);
	const [nbPeriodes, setNbPeriodes] = useState(0);
	const [periodesHoraires, setPeriodesHoraires] = useState({});
	const [locations, setLocations] = useState([]);
	const [filteredLocations, setFilteredLocations] = useState([]);
	const [events, setEvents] = useState([]);
	const [devisEvents, setDevisEvents] = useState([]);
	const [statusList, setStatusList] = useState([]);
	const [statusListSelected, setStatusListSelected] = useState([]);
	const [hideListSelected, setHideListSelected] = useState([]);
	const [hideProducts, setHideProducts] = useState(false);
	const [hideEmptyResources, setHideEmptyResources] = useState(false);
	const [hidePeriods, setHidePeriods] = useState(false);
	const [titleInfos, setTitleInfos] = useState({});
	const [eventPopoverProps, setEventPopoverProps] = useState({});

	const hideList = [
        {
            'value': 'products',
            'label': gctrad('Produits')
        },
        {
            'value': 'empty_resources',
            'label': gctrad('Salles vides')
        },
        {
            'value': 'periods',
            'label': gctrad('Périodes')
        }
    ];

	const getPersistedState = () => {
		const initialState = {
			view: initialView,
			dateStart,
			hotelsListSelected,
			hideListSelected
		}
		if (!calendarOnly) {
			return persistStateToSession(SESSION_STORAGE_KEY, initialState, true);
		}
		return initialState;
	}

	const setPersistedState = (state) => {
		if (!calendarOnly) {
			const persistedState = getPersistedState();
			persistStateToSession(SESSION_STORAGE_KEY, {
				...persistedState,
				...state
			});
		}
	}

	const removeEventListener = () => {
		window.removeEventListener('resize', setCalendarHeight);
		document.querySelector('.fc-toolbar-title')?.removeEventListener('click', handleDateSelect);
		document.removeEventListener('products-locations-sorted', handleLocationsSorted);
		document.removeEventListener('eventSaved', getEvents);

		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			let timelineBody = calendarApi.el.querySelector('.fc-timeline-body').closest('.fc-scroller');
			timelineBody?.removeEventListener('mousedown', handleGrabStart);
			timelineBody?.removeEventListener('mousemove', handleGrabMove);
			timelineBody?.removeEventListener('mouseup', handleGrabEnd);
			timelineBody?.removeEventListener('mouseleave', handleGrabEnd);

			let timelineHeader = calendarApi.el.querySelector('.fc-scrollgrid-section-header').lastChild.querySelector('.fc-scroller');
			timelineHeader?.removeEventListener('mousedown', handleGrabStart);
			timelineHeader?.removeEventListener('mousemove', handleGrabMove);
			timelineHeader?.removeEventListener('mouseup', handleGrabEnd);
			timelineHeader?.removeEventListener('mouseleave', handleGrabEnd);
		}
	}

	useEffect(() => {
		const persistedState = getPersistedState();
		setCurrentView(persistedState.view);
		setStartDate(persistedState.dateStart)
		setHideListSelected(persistedState.hideListSelected)
		getHotels();
		getStatus();

		if (props.windowId && props.windowId != '') {
			if (typeof window.LocationsCalendarOnQuit == 'undefined') window.LocationsCalendarOnQuit = () => false;
			window.LocationsCalendarOnQuit[props.windowId] = removeEventListener;
		}
	}, []);

	useEffect(() => {
		getTitleInfos();
		setCalendarHeight();
		onLoad();
	}, [isCalendarLoaded]);

	useEffect(() => {
		setCalendarHideParams();
	}, [hideListSelected]);

	useEffect(() => {
		getTitleInfos();
		displayCalendar();
		if (hotelsListSelected) {
			setPersistedState({ hotelsListSelected });
			if (linkBloquerSalleRef?.current) initPartLink(linkBloquerSalleRef.current);
			if (linkGererEspacesRef?.current) initPartLink(linkGererEspacesRef.current);
		}
	}, [hotelsListSelected])

	useEffect(() => {
		getEvents();
	}, [
		startDate,
		hotelsListSelected,
		statusListSelected,
		excludeRequestsHotelsId,
		showPopoverLinks,
		requestsHotelsId,
		productsId,
		baseCurrentUrl,
		currentView
	])

	useEffect(() => {
		if (variablesEvents) {
			getDevisEvents();
		}
	}, [isCalendarLoaded, variablesEvents]);

	useEffect(() => {
		displayCalendar();
	}, [hidePeriods]);

	useEffect(() => {
		filterTimelineResources();
	}, [
		events,
		devisEvents,
		hideEmptyResources
	])

	useEffect(() => {
		resizeTimelineFrame();
	}, [
		events,
		devisEvents,
		filteredLocations,
		hideProducts
	])

	const eventMinHeight = useMemo(() => {
		return hideProducts ? '65px' : '90px';
	}, [hideProducts]);

	const views = useMemo(() => {
		let slotMinTime;
		let slotDuration;
		let labels = {};
		switch (nbPeriodes) {
			case 0:
				slotMinTime = '00:00:00';
				slotDuration = '24:00:00';
				labels = {
					0: null
				};
				break;
			case 1:
				slotMinTime = '08:00:00';
				slotDuration = '16:00:00';
				labels = {
					8: periodesHoraires?.[1]?.label
				};
				break;

			case 2:
				slotMinTime = '08:00:00';
				slotDuration = '08:00:00';
				labels = {
					8: periodesHoraires?.[1]?.label,
					16: periodesHoraires?.[2]?.label
				};
				break;

			case 3:
				slotMinTime = '06:00:00';
				slotDuration = '06:00:00';
				labels = {
					6: periodesHoraires?.[1]?.label,
					12: periodesHoraires?.[2]?.label,
					18: periodesHoraires?.[3]?.label,
				};
				break;

			case 4:
				slotMinTime = '08:00:00';
				slotDuration = '04:00:00';
				labels = {
					8: periodesHoraires?.[1]?.label,
					12: periodesHoraires?.[2]?.label,
					16: periodesHoraires?.[3]?.label,
					20: periodesHoraires?.[4]?.label,
				};
				break;

		}
		return {
			semaine: {
				type: 'resourceTimelineWeek',
				duration: { days: 7 },
				slotMinTime: slotMinTime,
				slotDuration: slotDuration,
				slotLabelFormat: [
					{ weekday: 'short', day: 'numeric' },
					function (date) {
						let label = labels[date.start.hour];
						return label;
					}
				]
			},
			cinqjours: {
				type: 'resourceTimelineWeek',
				duration: { days: 5 },
				slotMinTime: slotMinTime,
				slotDuration: slotDuration,
				slotLabelFormat: [
					{ weekday: 'short', day: 'numeric' },
					function (date) {
						let label = labels[date.start.hour];
						return label;
					}
				]
			},
			resourceTimelineDay: {
				type: 'resourceTimelineDay',
				duration: { days: 1 },
				slotMinTime: slotMinTime,
				slotDuration: slotDuration,
				slotLabelFormat: [
					{ weekday: 'short', day: 'numeric' },
					function (date) {
						let label = labels[date.start.hour];
						return label;
					}
				]
			}
		};
	}, [nbPeriodes, periodesHoraires]);

	const getHotels = () => {
		axios
			.get(ENV.base_lang_url + "myspace/providers//ajaxGetter/get_hotels_proprietaire")
			.then((resp) => {
				if (resp.data.length) {
					setHotelsList(resp.data);
					const persistedState = getPersistedState();
					if (!persistedState.hotelsListSelected) {
						const selectedHotel = hotelsId ? resp.data.filter((s) => s.value == hotelsId).pop() : resp.data[0];
						persistedState.hotelsListSelected = selectedHotel;
						setPersistedState({ hotelsListSelected: persistedState.hotelsListSelected });
					}
					setHotelsListSelected(persistedState.hotelsListSelected);
				}
			})
			.catch((error) => {
				throw new Error(error);
			})
	}

	const getStatus = () => {
		axios
			.get(ENV.base_lang_url + "myspace/providers//ajaxGetter/get_all_locations_calendar_events_status")
			.then((resp) => {
				if (resp.data.length) {
					setStatusList(resp.data);
					const persistedState = getPersistedState();
					if (persistedState.statusListSelected) {
						setStatusListSelected(persistedState.statusListSelected);
					}
				}
			})
			.catch((error) => {
				throw new Error(error);
			})
	}

	const getEvents = () => {
		const { hotelsListSelected } = getPersistedState();
		if (isCalendarLoaded && startDate && hotelsListSelected?.value && calendarRef?.current) {
			let calendarApi = calendarRef?.current?.getApi();
			const usePeriodes = currentView != 'resourceTimelineMonth' ? 1 : 0;
			const dateStart = dayjs(String(startDate));
			const dateEnd = dayjs(String(endDate ?? calendarApi.view.currentEnd));
			axios
				.get(ENV.base_lang_url + "myspace/providers//locations_calendar/ajax/get_events_by_hotel", {
					params: {
						hotels_id: hotelsListSelected.value,
						exclude_requests_hotels_id: excludeRequestsHotelsId,
						show_popover_links: showPopoverLinks ? 1 : 0,
						current_requests_hotels_id: requestsHotelsId,
						current_products_id: productsId,
						base_current_url: baseCurrentUrl,
						use_periodes: usePeriodes,
						statut: selectedValuesToArray(statusListSelected),
						start: dateStart.format('YYYY/MM/DD'),
						end: dateEnd.format('YYYY/MM/DD')
					}
				})
				.then((resp) => {
					let evts = resp.data ?? [];
					if (currentView === 'resourceTimelineMonth' || hidePeriods) {
						evts = recalcEventsFullDayPeriod(evts);
					}
					setEvents(evts);
				})
				.catch((error) => {
					throw new Error(error);
				})
		}
	}

	const getDevisEvents = () => {
		if (isCalendarLoaded && startDate && hotelsListSelected?.value) {
			let variables = { ...variablesEvents };
			let eventsJson = JSON.stringify(variables);
			axios.post(
				ENV.base_lang_url + "myspace/providers//locations_calendar/ajax/format_events",
				{
					hotels_id: hotelsListSelected.value,
					requests_hotels_id: requestsHotelsId,
					events: eventsJson
				},
				{ headers: { 'Content-Type': 'multipart/form-data' } }
			)
				.then((resp) => {
					setDevisEvents(resp.data ?? []);
				})
				.catch((error) => {
					throw new Error(error);
				})
		}
	}

	const getTitleInfos = () => {
		let calendarApi = calendarRef?.current?.getApi();
		if (!calendarOnly && calendarApi && hotelsListSelected?.label) {
			const datesPeriod = calendarApi.el.querySelector('.fc-toolbar-title')?.innerText ?? '';
			const title = {
				hotel: gctrad('[:gps] Calendrier des salles') + ' - ' + hotelsListSelected.label,
				datesPeriod
			};
			setTitleInfos(title);
		}
	}

	const displayCalendar = () => {
		if (hotelsListSelected?.value) {
			axios
				.post(
					ENV.base_lang_url + "myspace/providers//locations_calendar/ajax/display_calendar",
					{ hotels_id: hotelsListSelected.value },
					{ headers: { 'Content-Type': 'multipart/form-data' } }
				)
				.then((resp) => {
					const { data } = resp;
					if (!isEmptyObject(data ?? {})) {
						setLocations(data.locations);
						setFilteredLocations(data.locations);
						setPeriodesHoraires(data.periodes_horaires);
						setNbPeriodes(!hidePeriods ? data.nb_periodes : 0);
						setIsCalendarLoaded(true);
					}
				})
				.catch((error) => {
					throw new Error(error);
				})
		}
	}

	const setCalendarHeight = () => {
		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			setResourceAreaWidth(window.innerWidth <= 768 ? '110px' : '220px');
			if (resizeHeight) {
				const section = calendarApi.el.closest('section');
				if (section) {
					const divForm = section.querySelector('.fixed-part');
					if (divForm) {
						const divLegende = section.querySelector('.legendes');
						const legendes_height = divLegende ? divLegende.offsetHeight : 0;
						const height = section.offsetHeight - divForm.offsetHeight - legendes_height - resizeHeight;
						if (height > 100) {
							calendarApi.setOption('height', height);
						}
					}
				}
			}
		}
	}

	const recalcEventsFullDayPeriod = (events) => {
		let evts = [];
		events.map((event) => {
			const { start, end } = event;
			const startDate = dayjs(String(start.substring(0, start.indexOf(' '))));
			const endDate = dayjs(String(end.substring(0, end.indexOf(' '))));
			event.extendedProps.periodes = { 1: 'fullday'};
			event.extendedProps.groupedEvents?.map((subEvent) => {
				subEvent.extendedProps.num_periode = 1;
			})

			evts.push({
				...event,
				start: startDate.startOf('date').format('YYYY-MM-DD HH:mm:ss'),
				end: endDate.endOf('date').format('YYYY-MM-DD HH:mm:ss')
			})
		})
		return evts;
	}

	const filterTimelineResources = () => {
		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			let filteredResources = [];
			[...locations].map((resource) => {
				const hasEvent = Boolean(events.filter((e) => e.resourceId == resource.id).length);
				// Masquage des salles vides
				if (!hideEmptyResources || (hideEmptyResources && hasEvent)) {
					filteredResources.push(resource);
                }
			})
			setFilteredLocations(filteredResources);
		}
	}

	const resizeTimelineFrame = () => {
		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			let calendarRows = calendarApi.el.querySelectorAll('.fc-timeline-lane-frame');
			[...calendarRows].map((row) => {
				const resourceId = row.closest('td').dataset.resourceId;
				const resourceSlot = calendarApi.el.querySelector(`.fc-datagrid-cell.fc-resource[data-resource-id="${resourceId}"]`);
				const eventRow = row.querySelector('.fc-timeline-events.fc-scrollgrid-sync-inner');
				const eventSlots = row.querySelectorAll('.fc-timeline-event-harness');
				// Redimentionnement de la timeline
				if (eventSlots.length) {
					const t = setTimeout(() => {
						row.style.height = resourceSlot.querySelector('.fc-datagrid-cell-frame').style.height = eventRow.style.height;
						clearTimeout(t);
					}, 100);
				} else {
					row.style.height = resourceSlot.querySelector('.fc-datagrid-cell-frame').style.height = eventMinHeight;
				}
			})
		}
	}

	const setCalendarHideParams = () => {
		const isHideProducts = Boolean([...hideListSelected].filter((h) => h.value === 'products').shift());
		const isHideEmptyResources = Boolean([...hideListSelected].filter((h) => h.value === 'empty_resources').shift());
		const isHidePeriods = Boolean([...hideListSelected].filter((h) => h.value === 'periods').shift());
		setHideProducts(isHideProducts);
		setHideEmptyResources(isHideEmptyResources);
		setHidePeriods(isHidePeriods);
	}

	const handleChangeDates = (e) => {
		switch (e.view.type) {
			case 'semaine':
			case 'cinqjours':
				setSlotMinWidth(110);
				break;
			case 'resourceTimelineMonth':
				setSlotMinWidth(85);
				break;
			default:
				setSlotMinWidth(null);
				break;
		}

		setEventPopoverProps({});
		setPersistedState({
			view: e.view.type,
			dateStart: e.start
		});
		setCurrentView(e.view.type);
		setStartDate(e.start);
		setEndDate(e.end);
		getTitleInfos();
	}

	const handleDateSelect = () => {
		setOpenDateSelect(!openDateSelect);
	}

	const handleLocationsSorted = (e) => {
		const { detail: hotelId } = e;
		if (parseInt(hotelsListSelected?.value) === hotelId) {
			displayCalendar();
			resizeTimelineFrame();
		}
	}

	const handleChangeView = (view) => {
		let calendarApi = calendarRef?.current?.getApi();
		calendarApi.changeView(view);
	}

	const handleChangeHideList = (hide) => {
		let selectedHideList = [];
		if (hide) {
			selectedHideList = [...hideListSelected];
			if (selectedHideList.filter((h) => h.value == hide.value).length > 0) {
				const index = selectedHideList.map(h => h.value).indexOf(hide.value);
				if (index > -1) selectedHideList.splice(index, 1);
			} else {
				selectedHideList.push(hide);
			}
		}

		setPersistedState({ hideListSelected: selectedHideList });
		setHideListSelected(selectedHideList);
	}

	const handleChangeStatut = (statut) => {
		let selectedStatus = [];
		if (statut) {
			selectedStatus = [...statusListSelected];
			if (selectedStatus.filter((s) => s.value == statut.value).length > 0) {
				const index = selectedStatus.indexOf(statut);
				if (index > -1) selectedStatus.splice(index, 1);
			} else {
				selectedStatus.push(statut);
			}
		}

		setPersistedState({ statusListSelected: selectedStatus });
		setStatusListSelected(selectedStatus);
	}

	const handleEventClick = (eventInfo) => {
		const {
			event,
			el: anchorEl
		} = eventInfo;
		setEventPopoverProps({ event, anchorEl });
	}

	const handleGrabStart = (event) => {
		event.preventDefault();
		const { currentTarget: el } = event;
		timelineDrag = {
			mouseDown: true,
			startX: event.pageX - el.offsetLeft,
			scrollLeft: el.scrollLeft
		};
		el.classList.add('grabbing');
	}

	const handleGrabMove = (event) => {
		event.preventDefault();
		const { mouseDown, startX, scrollLeft } = timelineDrag;
		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			let timelineBody = calendarApi.el.querySelector('.fc-timeline-body').closest('.fc-scroller');
			if (mouseDown) {
				const { currentTarget: el } = event;
				const x = event.pageX - el.offsetLeft;
				const scroll = (x - startX) * 2; // scroll-fast
				timelineBody.scrollLeft = scrollLeft - scroll;
			}
		}
	}

	const handleGrabEnd = () => {
		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			timelineDrag.mouseDown = false;
			Array.from(calendarApi.el.querySelectorAll('.grabbing')).map((el) => {
				el.classList.remove('grabbing');
			})
		}
	}

	const handleExportPDF = () => {
		let params = new URL(document.location.toString()).searchParams;
		let debugDom = params.get(DEBUG_PDF_QUERY_PARAM) !== null;

		let calendarApi = calendarRef?.current?.getApi();
		if (calendarApi) {
			if (!debugDom) {
				nav.wait({ display: true, delay: 100, text: gctrad('Génération du fichier PDF en cours') });
			}
			const filename = formatXlsExportFilename(currentSociete.nom, gctrad('[:gps] Calendrier des salles'));
			const { pdfRoot, container } = createPdfRoot(calendarApi.el, debugDom);

			pdfRoot.render(
				<LocationsCalendarPdfComponent
					ref={printRef}
					initialDate={startDate}
					initialView={currentView}
					views={views}
					slotLabelContent={renderSlotLabelContent}
					resourceLabelContent={renderResourceLabelContent}
					locations={filteredLocations}
					events={[...events, ...devisEvents]}
					eventOrder={[ "start", "-duration", "allDay", (a, b) => a?.extendedProps?.time_start_ts - b?.extendedProps?.time_start_ts ]}
					eventContent={(eventInfo) => renderEventContent(eventInfo, hideProducts, eventMinHeight)}
					nbPeriodes={nbPeriodes}
					hideProducts={hideProducts}
					hideNonAttribues={true}
					debugDom={debugDom}
					titleInfos={titleInfos}
					callback={async () => {
						if (debugDom) return false;
						const canvas = await html2canvas(printRef.current, {
							scale: window.devicePixelRatio, // Utiliser la résolution native
							width : 1500
						});

						const doc = new jsPDF({
							orientation: 'l',
							unit: 'mm',
							format: 'a4',
							compress: true
						});

						let totalPagesExp = '{total_pages_count_string}';
						const imgWidth = 287;
						const pageHeight = 210;
						let marginLeft = 5;
						let marginTop = 0;
						const imgHeight = (canvas.height * imgWidth) / canvas.width;
						let heightLeft = imgHeight;

						doc.setFontSize(8);
						doc.addImage(canvas, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);
						// Footer
						doc.text((doc.internal.getNumberOfPages() + '/' + totalPagesExp), imgWidth + 35, pageHeight - 5, { align: 'right' });
						heightLeft -= pageHeight;

						while (heightLeft >= 0) {
							marginTop = heightLeft - imgHeight;
							doc.addPage();
							doc.addImage(canvas, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);
							// Footer
							doc.text((doc.internal.getNumberOfPages() + '/' + totalPagesExp), imgWidth + 35, pageHeight - 5, { align: 'right' });
							heightLeft -= pageHeight;
						}

						doc.putTotalPages(totalPagesExp);
						doc.save(`${filename}.pdf`);

						unmountPdfRoot(pdfRoot, container);
						nav.wait(false);
					}}
				/>
			);
		}
	}

	const right_hotelier_produits = !!AUTH.membres_rights.includes('hotelier_produits');

	return (
		<ThemeProvider theme={backYouTheme}>
			<div className={`locations-calendar-container nb-periodes${nbPeriodes} mt-3`}>
				{!isCalendarLoaded ? (
					<>
						<Skeleton
							className="card bg-gc-light-blue-dark border-none mb-3"
							variant="rectangular"
							width="100%"
							height={40}
						/>
						<Skeleton
							className="card bg-gc-light-blue-dark border-none my-4"
							variant="rectangular"
							width="100%"
							height={600}
						/>
					</>
				) : (
					<Box className={hidePeriods ? 'fc-hide-periods' : 'fc-show-periods'}>
						{!calendarOnly && (
							<div className="d-flex justify-content-between text-gc-dark-blue border-light border-bottom pb-2 mb-2">
								<div className="form-group col-12 col-md-4 p-0 m-0 mr-md-2 mb-2">
									<Select
										parentFunction={setHotelsListSelected}
										slug={'hotels'}
										label=''
										placeHolder={gctrad('[:myspace] Etablissement')}
										items={hotelsList}
										selectedItems={hotelsListSelected}
										autoTrad={false}
										multiple={false}
										clearable={false}
									/>
								</div>
								<div>
									
									<a
										ref={linkBloquerSalleRef}
										href='#'
										data-part='+'
										data-target={'providers//locations_calendar/bloquer/' + hotelsListSelected?.value}
										className='btn btn-outline-gc-dark-blue js-nav font-weight-bold get_part_link mr-2'
									>
										<i className="fa-regular fa-xmark mr-2"></i>
										{gctrad('[:gps] Bloquer un espace')}
									</a>
									{right_hotelier_produits && (
										<a
											ref={linkGererEspacesRef}
											href='#'
											data-part='+'
											data-target={'providers//products/locations/' + hotelsListSelected?.value}
											className='btn btn-outline-gc-dark-blue js-nav font-weight-bold get_part_link mr-2'
										>
											<i className="fa-regular fa-light-ceiling mr-1"></i>
											{gctrad('[:myspace] Gérer vos espaces')}
										</a>
									)}
									
									<button
										className='btn btn-outline-gc-dark-blue js-nav font-weight-bold get_part_link'
										onClick={handleExportPDF}
									>
										<i className="fa-regular fa-file-pdf"></i>
										{!right_hotelier_produits && (
											<span className='ml-2'>{gctrad('Télécharger au format PDF')}</span>
										)}
									</button>
								</div>
							</div>
						)}
						<Box sx={{ position: 'relative' }}>
							<GcDatePicker
								open={openDateSelect}
								onOpen={() => setOpenDateSelect(true)}
								onClose={() => setOpenDateSelect(false)}
								type='date'
								value={startDate}
								parentFunction={(e) => calendarRef.current.getApi().gotoDate(e)}
								renderInput={({ inputRef, inputProps }) => (
									<TextField
										{...inputProps}
										ref={inputRef}
										type="hidden"
										value={inputProps.value}
										sx={{
											position: 'absolute',
											top: '2.8rem',
											left: 0,
											width: '30rem !important',
											"& fieldset": {
												border: 'none'
											}
										}}
									/>
								)}
							/>
							<CalendarRightToolbar className="position-md-absolute">
								<div className="d-flex justify-content-start justify-content-md-end align-items-end mb-4 mb-md-0">
									{!calendarOnly && (
										<>
											<DropdownSelect
												icon="fa-regular fa-eye-slash"
												btnClass="btn-gc-light-blue-dark text-gc-dark-blue font-weight-bold mr-2"
												parentFunction={handleChangeHideList}
												title={capitalize( gctrad('masquer') )}
												items={hideList}
												selectedItems={hideListSelected}
												autoTrad={true}
											/>
											<DropdownSelect
												icon="fa-regular fa-filter"
												btnClass="btn-gc-light-blue-dark text-gc-dark-blue font-weight-bold mr-2"
												parentFunction={handleChangeStatut}
												title={gctrad('statut')}
												items={statusList}
												selectedItems={statusListSelected}
												autoTrad={true}
											/>
										</>
									)}
									<Dropdown
										btnClass="btn-gc-light-blue-dark text-gc-dark-blue font-weight-bold"
										parentFunction={handleChangeView}
										icon="fa-regular fa-calendar-lines-pen"
										title={(currentView)?.trad()}
										items={['resourceTimelineMonth', ...Object.keys(views)]}
										selectedItem={currentView}
										autoTrad={true}
									/>
								</div>
							</CalendarRightToolbar>
						</Box>

						<FullCalendar
							id={id}
							slotMinWidth={slotMinWidth}
							ref={calendarRef}
							plugins={[resourceTimelinePlugin]}
							initialDate={startDate}
							datesSet={handleChangeDates}
							initialView={currentView}
							views={views}
							headerToolbar={{
								left: 'today prev,next title',
								right: ''
							}}
							buttonText={{
								'today': gctrad('[:gps] Aujourdhui')
							}}
							locale={ENV.lang.substring(0, 2)}
							height="auto"
							slotLabelContent={renderSlotLabelContent}
							resources={filteredLocations}
							resourceGroupField="groupId"
							resourceAreaWidth={resourceAreaWidth}
							resourceOrder="groupId,position,id"
							resourceAreaColumns={[{ headerContent: '' }]}
							resourceLabelContent={renderResourceLabelContent}
							resourceLabelClassNames={(e) => (
								e.resource.extendedProps.groupId == 2 ? 'resource-nonattribues' : ''
							)}
							resourceGroupLabelContent={(e) => (
								e.groupValue == 3 ? gctrad('[:new-front] Autres espaces') : ''
							)}
							resourceGroupLabelClassNames={(e) => {
								switch (e.groupValue) {
									case 1:
										return 'resourceGroup-mr';
									case 2:
										return 'resourceGroup-nonattribues';
									case 3:
										return 'resourceGroup-other';
								}
							}}
							resourceGroupLaneClassNames={(e) => {
								switch (e.groupValue) {
									case 1:
										return 'resourceGroup-mr';
									case 2:
										return 'resourceGroup-nonattribues';
									case 3:
										return 'resourceGroup-other';
								}
							}}
							viewDidMount={(e) => {
								window.addEventListener('resize', setCalendarHeight);
								document.querySelector('.fc-toolbar-title').addEventListener('click', handleDateSelect);
								document.addEventListener('products-locations-sorted', handleLocationsSorted);
								document.addEventListener('eventSaved', getEvents);

								let timelineBody = e.el.querySelector('.fc-timeline-body').closest('.fc-scroller');
								timelineBody.addEventListener('mousedown', handleGrabStart);
								timelineBody.addEventListener('mousemove', handleGrabMove);
								timelineBody.addEventListener('mouseup', handleGrabEnd);
								timelineBody.addEventListener('mouseleave', handleGrabEnd);

								let timelineHeader = e.el.querySelector('.fc-scrollgrid-section-header').lastChild.querySelector('.fc-scroller');
								timelineHeader.addEventListener('mousedown', handleGrabStart);
								timelineHeader.addEventListener('mousemove', handleGrabMove);
								timelineHeader.addEventListener('mouseup', handleGrabEnd);
								timelineHeader.addEventListener('mouseleave', handleGrabEnd);
							}}
							events={[...events, ...devisEvents]}
							eventOrder={[ "start", "-duration", "allDay", (a, b) => a?.extendedProps?.time_start_ts - b?.extendedProps?.time_start_ts ]}
							eventContent={(eventInfo) => renderEventContent(eventInfo, hideProducts, eventMinHeight)}
							eventClick={handleEventClick}
						/>

						{!Boolean([...events, ...devisEvents].length) && hideEmptyResources && (
							<NoResultCard 
								sx={{ 
									height: 180,
									backgroundColor: 'white'
								}} 
							/>
						)}
					</Box>
				)}
			</div>

			{!isCalendarLoaded ? (
				<Skeleton
					className="card bg-gc-light-blue-dark border-none my-4"
					variant="rectangular"
					width="100%"
					height={60}
				/>
			) : (
				<>
					<EventPopover {...eventPopoverProps} onClose={() => setEventPopoverProps({})} />
					<div className="legendes d-flex flex-wrap align-items-center py-4 px-2 bg-light">
						<div className="mr-4 small d-none d-md-block">{gctrad('[:myspace] Légende :')} </div>
						<div className="fc-event mr-2">
							<div className="fc-event-html lce_en_attente">
								<strong>{gctrad('[:locations_calendar_event] En attente')}</strong><br />
								<strong>{gctrad('[:locations_calendar_event] En cours')}</strong>
							</div>
						</div>
						<div className="fc-event mr-2">
							<div className="fc-event-html lce_sous_option">
								<strong>{gctrad('[:locations_calendar_event] Sous option')}</strong>
							</div>
						</div>
						<div className="fc-event mr-2">
							<div className="fc-event-html lce_reserve">
								<strong>{gctrad('[:locations_calendar_event] Réservé')}</strong>
							</div>
						</div>
						<div className="fc-event mr-2">
							<div className="fc-event-html lce_bloque">
								<strong>{gctrad('[:locations_calendar_event] Bloqué')}</strong>
							</div>
						</div>
						<div className="fc-event mr-2">
							<div className="fc-event-html conflit">
								<strong>{gctrad('[:locations_calendar_event] En conflit')}</strong>
							</div>
						</div>
						<div className="fc-event mr-2">
							<div className="fc-event-html lce_en_attente">
								<strong className="text-gc-orange">{gctrad('[:locations_calendar_event] Devis courant')}</strong>
							</div>
						</div>
					</div>
				</>
			)}
		</ThemeProvider>
	);
}

function renderSlotLabelContent(e) {
	return (
		<>
			{['resourceTimelineDay', 'resourceTimelineMonth', 'semaine', 'cinqjours'].includes(e.view.type) ? (
				e.level == 0 ? ( // Date
					<>
						<span className='fc-slot-label-jour'>{e.text.split(' ')[0].toUpperCase()}</span>
						<br />
						<span className='fc-slot-label-jourmois'>{e.text.split(' ')[1]}</span>
					</>
				) : ( // Heure
					<div className='fc-slot-label-periode text-center'>{e.text}</div>
				)
			) : (
				<span>{e.text?.replace(' ', '')}</span>
			)}
		</>
	)
}

function renderResourceLabelContent(eventInfo) {
	const { resource } = eventInfo;
	const { extendedProps: props } = resource;

	return (
		<div className="fc-resource-slot">
			<p className="font-weight-bold mb-0">{resource.title}</p>
			<div>
				{props.size > 0 && props.maxPax > 0 ? (
					<span>{`(${props.size}m2 - ${props.maxPax}pax)`}</span>
				) : (
					<>
						{props.size > 0 && (
							<span>{`(${props.size}m2)`}</span>
						)}
						{props.maxPax > 0 && (
							<span>{`(${props.maxPax}pax)`}</span>
						)}
					</>
				)}
			</div>
		</div>
	);
}

function renderEventContent(eventInfo, hideProducts, eventMinHeight) {
	const { event } = eventInfo;
	const status = event.extendedProps.status;
	const conflit = event.extendedProps.conflit;
	const currentDevis = (event.extendedProps.current_devis == 1);
	const periodes = event.extendedProps.periodes ?? {};
	const subEvents = event.extendedProps.groupedEvents ?? [];
	const displayProducts = !hideProducts && status !== 'lce_bloque';
	let firstPeriodeNum = parseInt(Object.keys(periodes).shift());
	let lastPeriodeNum = parseInt(Object.keys(periodes).pop());
	let periodesNb = lastPeriodeNum;
	if (firstPeriodeNum > 1) periodesNb = Object.keys(periodes).length;

	let lignesMeetingRooms = [];
	let lignesProduits = [];
	if (subEvents.length) {
		subEvents.map((subEvent) => {
			subEvent?.extendedProps?.lignes.map((ligne) => {
				if (ligne.meeting_rooms_type_slug) {
					if (lignesMeetingRooms.length < 1) lignesMeetingRooms.push(ligne);
				}
				else if (!ligne.meeting_rooms_type_slug && event.extendedProps.status !== 'lce_bloque') {
					ligne.extendedProps = subEvent.extendedProps;
					lignesProduits.push(ligne)
				}
			})
		})
	}

	return (
		<Event
			event-id={event.extendedProps.eventId}
			status={status}
			conflit={conflit}
			backgroundColor={event.backgroundColor}
			borderColor={event.extendedProps.status_color}
			minHeight={eventMinHeight}
			className="fc-event-card card rounded-0"
		>
			<Box
				className={`fc-event-card-period d-flex justify-content-between ${currentDevis ? 'text-gc-orange' : 'text-gc-dark-blue'}`}
				sx={{ fontSize: '.75em', padding: '4px 4px 0 4px' }}
			>
				<span>{event.extendedProps.time_start}</span>
				<span>{event.extendedProps.time_end}</span>
			</Box>

			<EventTitle
				className={`fc-event-card-title text-nowrap ${currentDevis ? 'text-gc-orange' : 'text-gc-dark-blue'}`}
				sx={{ fontSize: '1.2em' }}
			>
				{capitalize(event.title)}
			</EventTitle>

			{subEvents.length && (
				<>
					{lignesMeetingRooms.length > 0 && (
						lignesMeetingRooms.map((ligne, i) => {
							const currentProduct = (ligne.current_product == 1);
							return (
								<SubEventMeetingRoom
									key={i}
									cols={periodesNb}
									className={`fc-event-card-meeting-room text-nowrap ${currentProduct ? 'text-gc-orange font-weight-bold' : 'text-gc-dark-blue'}`}
									sx={{
										fontSize: '.85em',
										padding: '0 4px',
										paddingBottom: hideProducts ? 0 : '10px'
									}}
								>
									{ligne.pax > 0 && (
										<>
											<i className="fa-regular fa-user" style={{ paddingRight: '2px' }}></i>
											<span>x{ligne.pax}</span>
										</>
									)}
									{ligne.meeting_rooms_type_title && (
										<span style={{ paddingLeft: '2px' }}>{ligne.meeting_rooms_type_title}</span>
									)}
								</SubEventMeetingRoom>
							);
						})
					)}
					{lignesMeetingRooms.length === 0 && lignesProduits.length > 0 && (
						<Divider />
					)}
					{lignesProduits.length > 0 && (
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: `repeat(${periodesNb}, minmax(0,1fr))`,
								width: '100%',
								marginTop: 'auto'
							}}
						>
							{[...Array(periodesNb).keys()].map((i) => {
								const numPeriode = parseInt(Object.keys(periodes)[i]);

								return (
									<Box key={i}>
										{lignesProduits.map((ligne, idx) => {
											const currentProduct = (ligne.current_product == 1);
											const { extendedProps: evtProps } = ligne;
											const { num_periode: eventPeriode } = evtProps;
											return (
												displayProducts && (numPeriode == eventPeriode) && (
													<Box key={idx}>
														<SubEventProduct
															className={`fc-event-card-product text-nowrap ${currentProduct ? 'text-gc-orange font-weight-bold' : 'text-gc-dark-blue'}`}
															sx={{ fontSize: '.75em' }}
														>
															<span>&#x2022;</span>
															{ligne.qty > 0 && (
																<span className="fc-event-card-product-label" style={{ paddingRight: '2px' }}>{ligne.qty}</span>
															)}
															<span>{ligne.title}</span>
														</SubEventProduct>
													</Box>
												)
											);
										})}
									</Box>
								);
							})}
						</Box>
					)}
				</>
			)}
		</Event>
	)
}

const CalendarRightToolbar = muiStyled(Box)({
	top: 0,
	right: 0
});

const Event = muiStyled(Box)(({ status, conflit, backgroundColor, borderColor, minHeight }) => {
	let styles = {
		fontWeight: backYouTheme.typography.fontWeightRegular,
		minHeight: minHeight,
		background: status === 'lce_bloque' ? `repeating-linear-gradient(-45deg, white, white 2px, #DFDFDF 2px, #DFDFDF 4px)` : backgroundColor,
		border: 'none',
		borderBottom: `2px solid ${borderColor}`,
		paddingBottom: 4
	};
	if (conflit == 1) {
		styles = {
			...styles,
			borderBottom: '2px solid #dc3545'
		}
	}
	return styles;
});

const EventTitle = muiStyled(Typography)({
	width: '100%',
	fontSize: '1.1em',
	fontWeight: 900,
	lineHeight: '1.3',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	padding: '0 4px'
});

const SubEventMeetingRoom = muiStyled(Box)(({ cols }) => {
	if (cols < 2) {
		return {
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			width: '100%'
		}
	}
});

const SubEventProduct = muiStyled(Box)({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	width: '100%',
	padding: '0 2px'
});

const Divider = muiStyled(Box)({
	height: 18
});